import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import * as yup from "yup";
import { OpenInNewIcon, PaziaAsyncDropdown, PaziaTrashButton } from "components/atoms";
import { PlusButton } from "components/atoms/Buttons";
import { useTrainersOptions } from "api/hooks";
import { routes } from "consts";

// Trainer rates are no longer picked per schedule event — costs live in the "Koszty trenerów" module
// (trainer-costs/), which calculates them from the schedule. Here only the trainers are assigned.
export const BaseTrainersScheduleForm = (): JSX.Element => {
  const { control } = useFormContext<BaseTrainersScheduleFormSchema>();

  // setValue(TRAINERS, ...) from the topic pickers also flows into `fields`.
  const { fields, append, remove } = useFieldArray({
    control,
    name: BASE_TRAINERS_SCHEDULE_FORM_NAMES.TRAINERS,
    keyName: "key",
  });

  return (
    <div className="col-span-12 mt-4">
      <div className="text-primary-700 dark:text-primary-400 pb-1 text-center">Trenerzy</div>
      {fields.map((field, index) => (
        <ScheduleTrainerRow key={field.key} index={index} onRemove={() => remove(index)} />
      ))}
      <div className="col-start-12 flex justify-end">
        {/* @ts-ignore */}
        <PlusButton onClick={() => append({ trainerId: "", totalAmount: null })} />
      </div>
    </div>
  );
};

interface ScheduleTrainerRowProps {
  index: number;
  onRemove: () => void;
}
const ScheduleTrainerRow = ({ index, onRemove }: ScheduleTrainerRowProps): JSX.Element => {
  const { watch } = useFormContext<BaseTrainersScheduleFormSchema>();
  const selectedTrainers = watch(BASE_TRAINERS_SCHEDULE_FORM_NAMES.TRAINERS) ?? [];
  const trainerId = selectedTrainers[index]?.trainerId;
  const { isLoading: loadingTrainers, options: trainersOptions } = useTrainersOptions();

  // A trainer already assigned in another row can't be picked twice.
  const getOptions = () => {
    const takenIds = selectedTrainers
      .filter((x, idx) => idx !== index && x.trainerId)
      .map((x) => x.trainerId!.toString());
    return trainersOptions.filter((option) => !takenIds.includes(option.value + ""));
  };

  return (
    <div className="bg-primary-50 dark:bg-primary-800 relative mb-2 rounded p-2">
      {trainerId && <OpenInNewIcon href={routes.TRAINER(trainerId)} />}
      <div className="flex items-end gap-2">
        <PaziaAsyncDropdown
          className="grow"
          required
          isClearable={false}
          disabled={loadingTrainers}
          loadOptions={async () => getOptions()}
          label="Trener"
          loadOnRender
          name={`${BASE_TRAINERS_SCHEDULE_FORM_NAMES.TRAINERS}[${index}].trainerId`}
        />
        <PaziaTrashButton onClick={onRemove} />
      </div>
    </div>
  );
};

const BASE_TRAINERS_SCHEDULE_FORM_NAMES = {
  TRAINERS: "trainers",
} as const;

const scheduleTrainerSchema = yup.object({
  trainerId: yup.string().required(),
  // Legacy per-event price. Not editable anymore; kept only so editing an old event doesn't wipe it.
  totalAmount: yup.number().nullable(),
});
const baseTrainersScheduleSchema = yup
  .object({
    [BASE_TRAINERS_SCHEDULE_FORM_NAMES.TRAINERS]: yup.array().of(scheduleTrainerSchema),
  })
  .required();

const resolveDefaultValues = (modelTrainers?: { trainerId: number; totalPrice: number }[]) => {
  return {
    trainers:
      modelTrainers?.map((x) => ({
        trainerId: x.trainerId.toString(),
        totalAmount: x.totalPrice,
      })) ?? [],
  };
};

// Maps form rows to the API payload. totalPrice stays in the contract for old events.
const toRequestTrainers = (trainers?: ScheduleTrainerSchema[]) =>
  trainers?.map((x) => ({ trainerId: Number(x.trainerId), totalPrice: Number(x.totalAmount ?? 0) })) ?? [];

export type BaseTrainersScheduleFormSchema = yup.InferType<typeof baseTrainersScheduleSchema>;
export type ScheduleTrainerSchema = yup.InferType<typeof scheduleTrainerSchema>;

export const ScheduleFormTrainers = {
  schema: baseTrainersScheduleSchema,
  resolveDefaultValues,
  toRequestTrainers,
  fields: BASE_TRAINERS_SCHEDULE_FORM_NAMES,
};
